import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Check } from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import "./LandingPage.css";
import "./SundayBuilder.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TALLY_EMBED = "https://tally.so/embed/ODrYRA?alignLeft=1&transparentBackground=1&dynamicHeight=1";

// Any of these entitlements route straight to the planner — the
// institutional experience must not be interrupted by a marketing gate.
const ENTITLED_PLANS = ["parish_pro", "cathedral", "diocese"];

export default function SundayBuilder() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // user === null → still checking · false → unauthenticated · object → signed in
  const authChecking = user === null;
  const authed = user && typeof user === "object";
  const ent = authed ? user.entitlements : null;
  const entitled =
    !!ent && ENTITLED_PLANS.includes(ent.plan) && ent.plan_status === "active";

  const [gate, setGate] = useState("checking"); // checking | granted | denied
  const [plan, setPlan] = useState("");

  // Phase 2 routing correction · authenticated + entitled users must
  // never see the marketing gate. They land inside the workshop.
  useEffect(() => {
    if (authChecking) return;
    if (entitled) {
      navigate("/worship-planner", { replace: true });
    }
  }, [authChecking, entitled, navigate]);

  useEffect(() => {
    // Legacy Stripe-grant access token path (pre-auth-context users).
    // Only runs when we're not going to redirect to /worship-planner.
    if (authChecking) return;
    if (entitled) return; // redirecting — skip legacy check
    const legacy = localStorage.getItem("sb_access_token");
    const check = () => axios.get(`${API}/access/check`);
    const run = legacy
      ? axios.post(`${API}/access/adopt`, { token: legacy }).then(() => {
          localStorage.removeItem("sb_access_token");
          localStorage.removeItem("sb_plan");
          return check();
        })
      : check();
    run
      .then((res) => {
        setPlan(res.data.plan || "");
        setGate("granted");
      })
      .catch(() => setGate("denied"));
  }, [authChecking, entitled]);

  useEffect(() => {
    if (gate !== "granted") return;
    const id = "tally-embed-js";
    const load = () => window.Tally && window.Tally.loadEmbeds();
    if (document.getElementById(id)) {
      load();
      return;
    }
    const s = document.createElement("script");
    s.id = id;
    s.src = "https://tally.so/widgets/embed.js";
    s.onload = load;
    document.body.appendChild(s);
  }, [gate]);

  if (gate === "checking" || authChecking || entitled) {
    // Show the same checking state while we're either checking auth,
    // checking legacy grant, or in the middle of redirecting entitled
    // users to the workshop. Prevents the marketing gate from ever
    // flashing to an authenticated Parish Pro / Cathedral member.
    return (
      <div className="li-page sb-shell" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <p style={{ color: "var(--muted)" }} data-testid="sb-checking">Preparing your workshop…</p>
      </div>
    );
  }

  if (gate === "denied") {
    const features = [
      "SundayBuilder™ worship dossiers",
      "Parish Memory™",
      "Suggested Repertoire · Editorial Discernment",
      "Liturgical Alignment",
      "PDF & DOCX exports",
      "Report history",
      "Priority preparation",
    ];
    return (
      <div className="sbg" data-testid="sb-denied">
        <div className="sbg-card">
          <span className="sbg-eyebrow" data-testid="sb-denied-eyebrow">SundayBuilder™ · Membership</span>
          <h1 className="sbg-title" data-testid="sb-denied-title">
            Join the <em>Liturgical Intelligence™</em> membership.
          </h1>
          <p className="sbg-lead" data-testid="sb-denied-lead">
            For clergy, sacred music leaders, and diocesan ministry teams.
          </p>
          <p className="sbg-sub">
            Prepare complete worship dossiers grounded in Suggested Repertoire, Editorial Discernment,
            and Parish Memory™ — the interface should feel discovered, not generated.
          </p>

          <div className="sbg-divider" />
          <div className="sbg-includes">Membership includes</div>
          <ul className="sbg-features" data-testid="sb-denied-features">
            {features.map((f) => (
              <li key={f}><Check size={14} strokeWidth={2.5} /><span>{f}</span></li>
            ))}
          </ul>

          <button
            className="sbg-cta"
            onClick={() => navigate("/pricing")}
            data-testid="sb-go-pricing"
          >
            View Membership Plans
          </button>

          <div className="sbg-secondary">
            Already a member?
            <a href="/login" data-testid="sb-member-login">
              Sign in <span className="sbg-arrow">→</span>
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="li-page sb-shell">
      <div className="sb-topbar">
        <div className="li-wrap" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", height: 72 }}>
          <a href="/" className="li-brand" data-testid="sb-brand">
            Liturgical<span>Intelligence</span>™
          </a>
          {plan && <div className="sb-plan-pill" data-testid="sb-plan">{plan}</div>}
        </div>
      </div>

      <div className="li-wrap sb-body">
        <div className="li-eyebrow">SundayBuilder™ Intake</div>
        <h1 className="sb-h1">Tell us about the service you're planning.</h1>
        <p className="sb-sub">
          The more context you provide — the people, the place, the liturgy, and the pastoral moment —
          the more faithful your plan will be.
        </p>

        <div className="sb-tally" data-testid="sb-tally-form">
          <iframe
            data-tally-src={TALLY_EMBED}
            loading="lazy"
            width="100%"
            height="1100"
            frameBorder="0"
            marginHeight="0"
            marginWidth="0"
            title="SundayBuilder™ Intake"
          />
        </div>
      </div>
    </div>
  );
}
